import { sleep } from '../../../sleep';

import type { TypeResolvedFeatures, TypeUploadOnceResult } from '../types';
import type { TypeAttemptFn, TypeUploadFeature, TypeUploadPart } from '../core/types';
import { isRetryableStatus } from '../utils';

// ----------------------------------------------------------------------------

export type TypeRetryFeatureDeps = {
    isAborted: () => boolean;
    waitForResume?: () => Promise<void>;
    // Called once per failed attempt that is about to be retried; `attempt` counts from 1.
    fnRetry?: (attempt: number, result: TypeUploadOnceResult) => void;
};

// ----------------------------------------------------------------------------

const RETRY_POLL_MS = 100;

/*
 * Waits out the backoff in slices so an abort during the delay is noticed right away instead of
 * only on the next attempt. Returns false when the wait was cut short.
 */
async function waitBeforeRetry(delayMs: number, isAborted: () => boolean): Promise<boolean> {
    let remaining = delayMs;

    while (remaining > 0) {
        if (isAborted()) {
            return false;
        }

        const slice = Math.min(RETRY_POLL_MS, remaining);

        await sleep(slice);

        remaining -= slice;
    }

    return !isAborted();
}

// ----------------------------------------------------------------------------

/*
 * Re-runs a failed attempt, with a linear backoff.
 *
 * Uses `wrap` rather than the request hooks: it does not care what a request does, only that the
 * whole attempt can be run again. That is also why it composes with anything else that wraps —
 * chunking, when it arrives, sits outside it so each chunk is retried on its own.
 *
 * Only transient failures are retried. A result flagged `retryable: false` (a deterministic empty
 * body) and a deterministic status both stop the loop at once, as does an abort.
 */
function createRetryFeature(features: TypeResolvedFeatures, deps: TypeRetryFeatureDeps): TypeUploadFeature | null {
    const { enabled, max, baseMs, fnIsRetryable } = features.retry;

    if (!enabled || max <= 0) {
        return null;
    }

    const fnCanRetry = (result: TypeUploadOnceResult): boolean => {
        if (result.retryable === false || deps.isAborted()) {
            return false;
        }

        if (typeof fnIsRetryable === 'function') {
            return fnIsRetryable(result);
        }

        const isTransportFailure =
            result.reason === 'error' || result.reason === 'timeout' || result.reason === 'failed';

        return isTransportFailure && isRetryableStatus(result.status ?? 0);
    };

    return {
        name: 'retry',
        wrap:
            (next: TypeAttemptFn): TypeAttemptFn =>
            // `part` is forwarded, so a chunked upload retries the chunk and not the whole file.
            async (part?: TypeUploadPart): Promise<TypeUploadOnceResult> => {
                let lastResult: TypeUploadOnceResult = { ok: false, status: 0, reason: 'abort' };

                /*
                 * A run cancelled between attempts is a cancellation, not the failure that happened
                 * to come before it. Returning `lastResult` here would report the previous 503 —
                 * and because that carries a server response, `classifyOutcome` would bucket the
                 * file as an error and fire fnFileError for an abort the caller asked for.
                 *
                 * A failure that itself raised the abort flag is the other case, and it does return
                 * `lastResult`: there the status is the reason the run stopped, so it must survive.
                 */
                const fnAborted = (): TypeUploadOnceResult => ({
                    ok: false,
                    xhr: lastResult.xhr,
                    status: 0,
                    reason: 'abort',
                });

                for (let attempt = 0; attempt <= max; attempt++) {
                    /*
                     * Re-checked here and not only in the outer gate: the run can be paused or
                     * aborted during a backoff, and an abort must not stay parked behind a pause.
                     */
                    if (attempt > 0) {
                        if (deps.isAborted()) {
                            return fnAborted();
                        }

                        if (deps.waitForResume) {
                            await deps.waitForResume();
                        }

                        if (deps.isAborted()) {
                            return fnAborted();
                        }
                    }

                    lastResult = await next(part);

                    if (lastResult.ok || attempt === max || !fnCanRetry(lastResult)) {
                        return lastResult;
                    }

                    deps.fnRetry?.(attempt + 1, lastResult);

                    if (!(await waitBeforeRetry(baseMs * (attempt + 1), deps.isAborted))) {
                        return fnAborted();
                    }
                }

                return lastResult;
            },
    };
}

// ----------------------------------------------------------------------------

export default createRetryFeature;
